import React, { useEffect } from "react";
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Group,
  HoverCard,
  Menu,
  Modal,
  Switch,
  Text,
  Textarea,
  createStyles,
} from "@mantine/core";
import { IconDownload, IconEdit, IconTrash } from "@tabler/icons-react";
import { FiExternalLink } from "react-icons/fi";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import swal from "sweetalert";
import {
  editPatient,
  removePatient,
  updateClientStatus,
} from "../../services/patient";
import PatientModal from "../PatientModal";
import { useDispatch, useSelector } from "react-redux";
import { decrypt } from "../../utils";
import { useNavigate } from "react-router-dom";
import { BiDotsVerticalRounded } from "react-icons/bi";
import { setSelectedClient } from "../../store/reducers/mar";
import moment from "moment";
import { pdf } from "@react-pdf/renderer";
import ClientSummaryPDF from "../ClientSummaryPDF/ClientSummaryPDF";
import { getActivites } from "../../services/activity";
import { saveAs } from "file-saver";
import { DateInput } from "@mantine/dates";
import DischargeReasonAndDate from "../DischargeReasonAndDate";
import { updateStaffStatus } from "../../services/staff";

const useStyles = createStyles((theme) => ({
  /* ── avatar ring ── */
  avatarRing: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    border: "2px solid var(--app-primary)",
    padding: 2,
    flexShrink: 0,
    boxSizing: "content-box",
    background: "var(--app-surface-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  avatarInner: {
    width: "100%",
    height: "100%",
    borderRadius: "50%",
    background: "var(--app-surface-2)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 13,
    color: "var(--app-text)",
    overflow: "hidden",
  },

  /* ── client cell ── */
  clientCell: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    minWidth: 220,
  },
  clientName: {
    fontSize: 14,
    fontWeight: 600,
    color: "var(--app-text)",
    letterSpacing: "-0.005em",
    lineHeight: 1.3,
  },
  clientId: {
    fontFamily: "'SF Mono', 'Menlo', monospace",
    fontSize: 11.5,
    color: "var(--app-text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    marginTop: 1,
  },

  /* ── facility chip ── */
  facilityChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    fontSize: 11.5,
    fontWeight: 600,
    background: "var(--app-surface-2)",
    color: "var(--app-text)",
    border: "1px solid var(--app-border)",
    borderRadius: 5,
    marginRight: 4,
    marginBottom: 2,
    whiteSpace: "nowrap",
  },

  /* ── mono cells (dates, phone) ── */
  cellMono: {
    fontFamily: "'SF Mono', 'Menlo', monospace",
    fontSize: 12.5,
    color: "var(--app-text)",
  },

  /* ── active/inactive switch ── */
  switch: {
    "& .mantine-Switch-track": {
      background: "var(--app-border)",
      borderColor: "var(--app-border)",
    },
    "& input:checked + .mantine-Switch-track": {
      background: "var(--app-primary)",
      borderColor: "var(--app-primary)",
    },
  },

  /* ── actions cell ── */
  actionsCell: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    position: "relative",
  },

  /* ── row cursor ── */
  row: {
    cursor: "pointer",
  },

  /* ── avatar image ── */
  avatarImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    borderRadius: "50%",
  },

  /* ── name wrap ── */
  nameWrap: {
    minWidth: 0,
  },

  /* ── facility chips wrap ── */
  chipsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },

  /* ── guardian name cell ── */
  cellText: {
    color: "var(--app-text)",
    fontSize: 13,
  },

  /* ── discharge reason text ── */
  dischargeText: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: 150,
    fontSize: 13,
    color: "var(--app-text)",
  },

  /* ── discharge/misc ── */
  truncate: {
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    maxWidth: 150,
  },
  dischargeBgColor: {
    backgroundColor: "var(--app-surface)",
  },
  datePicker: {
    width: 250,
    "& .mantine-DatePickerInput-input": {
      border: "none",
      borderRadius: "0px !important",
      borderBottom: "2px solid var(--app-border) !important",
    },
    "@media (max-width: 600px)": {
      width: "100%",
    },
  },
  buttons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: ".5rem",
    marginTop: 20,
    marginBottom: 20,
  },
  marginTop: {
    marginTop: 0,
  },
}));

const PatientTableRow = ({ row, onEdit, selectedTab }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { classes } = useStyles();
  const [active, setActive] = useState(row.active);
  const [opened, { open, close }] = useDisclosure(false);
  const [view, setView] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [inActiveReason, setInActiveReason] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [value, setValue] = useState(new Date());

  useEffect(() => {
    setActive(row.active);
  }, [row]);

  const { profile } = useSelector((state) => state.profile);

  const customId = row?.id;
  const clientId = customId?.slice(-6);

  // functions
  const remove = async () => {
    const willDelete = await swal({
      title: `Are you sure?`,
      text: "This action cannot be undone.",
      icon: "warning",
      dangerMode: true,
      buttons: ["Cancel", "Delete"],
    });
    if (willDelete) {
      try {
        await removePatient(row?.id, () => {}, selectedTab);
        swal("Success", "Client remove successfully", "success");
      } catch (reasonError) {
        swal("Error", "Failed to remove the Client", "error");
      }
    }
  };

  const handleClientSelect = (obj) => {
    dispatch(setSelectedClient(obj));
    localStorage.setItem("selectedClient", JSON.stringify(obj));
  };

  const updateStatus = async (event) => {
    if (!event.target.checked) {
      setDisableModalOpen(true);
    } else {
      setActive(false);
      setValue("");
      setInActiveReason("");
      await updateClientStatus(
        { id: row?.id, active: true },
        true,
        selectedTab,
      );
    }
  };

  function handleRowClick(event) {
    handleClientSelect(row);
    console.log("🚀 ~ handleRowClick ~ row:", row);
    navigate("/home/client", { state: row?.id });
  }

  const _getActivites = async () => {
    try {
      const data = await getActivites(customId);
      return data;
    } catch (error) {
      console.error("Failed to fetch activities:", error);
    }
  };

  const handlePdfDownload = async (fileName) => {
    setPdfLoading(true);
    const activitiesRes = await _getActivites();
    const blob = await pdf(
      <ClientSummaryPDF
        clientData={row}
        activities={activitiesRes}
        facilityName={profile?.business?.companyName || profile?.facility?.name}
        facilityPhoto={
          profile?.facility?.imageurl
            ? decrypt(profile.facility.imageurl)
            : null
        }
      />,
    ).toBlob();
    saveAs(blob, fileName);
    setPdfLoading(false);
  };

  useEffect(() => {
    // Convert inactiveClientDate to Date object if it exists
    const initialDate = row?.inactiveClientDate
      ? new Date(row.inactiveClientDate)
      : new Date();

    setValue(initialDate);
    // setReason(row?.inActiveReason);
    setInActiveReason(row?.inActiveReason);
  }, [row]);

  const handleDischargeSave = async () => {
    if (inActiveReason.trim() === "") {
      setReasonError("Discharge reason is required.");
      return;
    }
    setActive(false);

    await updateClientStatus(
      {
        id: row?.id,
        active: false,
        inActiveReason,
        inactiveClientDate: value,
      },
      true,
      selectedTab,
    );
    setDisableModalOpen(false);
  };

  const handleDischargeClose = () => {
    setInActiveReason("");
    setReasonError("");
    setDisableModalOpen(false);
  };

  const initials = row?.name
    ? row.name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase()
    : "?";

  const avatarSrc = decrypt(row?.imageurl);

  return (
    <>
      <tr className={classes.row}>
        {/* Client name + avatar */}
        <td onClick={handleRowClick}>
          <Box className={classes.clientCell}>
            <Box className={classes.avatarRing}>
              <Box className={classes.avatarInner}>
                {avatarSrc ? (
                  <img
                    src={avatarSrc}
                    alt={row?.name}
                    className={classes.avatarImg}
                  />
                ) : (
                  initials
                )}
              </Box>
            </Box>
            <Box className={classes.nameWrap}>
              <Text className={classes.clientName}>{row?.name || "-"}</Text>
              <Text className={classes.clientId}>{clientId || "-"}</Text>
            </Box>
          </Box>
        </td>

        {/* Facility chips */}
        <td onClick={handleRowClick}>
          <Box className={classes.chipsWrap}>
            {row?.facilityId?.length
              ? row.facilityId.map((fac, i) => (
                  <span key={i} className={classes.facilityChip}>
                    {fac?.name}
                  </span>
                ))
              : "-"}
          </Box>
        </td>

        {/* Intake date */}
        <td onClick={handleRowClick}>
          <Text className={classes.cellMono}>
            {moment(row.form1?.admissionDate).format("MM/DD/YYYY") || "-"}
          </Text>
        </td>

        {/* DOB */}
        <td onClick={handleRowClick}>
          <Text className={classes.cellMono}>
            {moment(row.form1?.dob).format("MM/DD/YYYY") || "-"}
          </Text>
        </td>

        {/* Guardian name */}
        <td onClick={handleRowClick}>
          <Text className={classes.cellText}>
            {row.form2?.emergencyContactName || "-"}
          </Text>
        </td>

        {/* Guardian phone */}
        <td onClick={handleRowClick}>
          <Text className={classes.cellMono}>
            {row.form2?.emergencyContactPhoneNumber || "-"}
          </Text>
        </td>

        {/* Inactive-only columns */}
        {selectedTab !== true && (
          <>
            <td>
              <HoverCard width={300} shadow="md" position="bottom" withArrow>
                <HoverCard.Target>
                  <Text
                    onClick={handleRowClick}
                    className={classes.dischargeText}
                  >
                    {row?.inActiveReason?.slice(0, 100) || "-"}
                    {row?.inActiveReason?.length > 100 && "..."}
                  </Text>
                </HoverCard.Target>
                <HoverCard.Dropdown>
                  <Text>{row?.inActiveReason || "No reason provided"}</Text>
                </HoverCard.Dropdown>
              </HoverCard>
            </td>
            <td onClick={handleRowClick}>
              <Text className={classes.cellMono}>
                {moment(row?.inactiveClientDate).format("MM/DD/YYYY") || "-"}
              </Text>
            </td>
          </>
        )}

        {/* Active toggle + menu */}
        <td>
          <Box className={classes.actionsCell}>
            <Switch
              size="sm"
              className={classes.switch}
              checked={active}
              onChange={updateStatus}
              disabled={
                profile?.user?.type === "staff" &&
                profile?.user?.role !== "admin"
              }
            />
            <PatientModal
              opened={opened}
              close={close}
              title={"Edit Individual"}
              data={row}
              view={view}
              setView={setView}
            />
            <Menu shadow="md" position="left" zIndex={1000}>
              <Menu.Target>
                <ActionIcon variant="subtle" color="dark" radius="sm">
                  <BiDotsVerticalRounded
                    id="vertical-dots"
                    size={20}
                    color="var(--app-text)"
                  />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  id="open-link"
                  icon={<FiExternalLink size={14} />}
                  onClick={() => {
                    navigate("/home/client", { state: row?.id });
                    handleClientSelect(row);
                  }}
                >
                  Open facesheet
                </Menu.Item>
                <Menu.Item
                  disabled={pdfLoading}
                  icon={<IconDownload size={14} />}
                  onClick={() =>
                    handlePdfDownload(
                      `${row?.form1?.name ?? "Client Summary"}.pdf`,
                    )
                  }
                >
                  Download summary
                </Menu.Item>
                {!(
                  profile.user.type === "staff" && profile.user.role === "staff"
                ) && (
                  <Menu.Item
                    icon={<IconEdit size={14} />}
                    onClick={() => onEdit(row)}
                  >
                    Edit client
                  </Menu.Item>
                )}
                {row?.active === false && (
                  <Menu.Item
                    icon={<IconEdit size={14} />}
                    onClick={updateStatus}
                  >
                    Discharge Info
                  </Menu.Item>
                )}
                <Menu.Divider />
                {!(
                  profile.user.type === "staff" && profile.user.role === "staff"
                ) && (
                  <Menu.Item
                    icon={<IconTrash size={14} />}
                    color="red"
                    onClick={remove}
                  >
                    Delete
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          </Box>
        </td>
      </tr>
      {/* <----- Call modal conditionally to prevent unnecessary calls -----> */}

      {disableModalOpen && (
        <DischargeReasonAndDate
          value={value}
          setValue={setValue}
          row={row}
          selectedTab={selectedTab}
          disableModalOpen={disableModalOpen}
          handleDischargeClose={handleDischargeClose}
          reasonError={reasonError}
          inActiveReason={inActiveReason}
          handleDischargeSave={handleDischargeSave}
          setInActiveReason={setInActiveReason}
          setReasonError={setReasonError}
        />
      )}
    </>
  );
};

export default PatientTableRow;
